import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Droplets, ShieldCheck, Clock } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const ENSUITES_HERO = "/products/ensuites/731077_12929e6f6f6244a9adc301bdb5dcaf71~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Already-native products elsewhere on the site — cross-linked here, not duplicated
const ENSUITE_LINKED_PRODUCTS = [
  { name: "Quick Pitch Heavy Duty Ensuite Floor", price: 139, thumb: "/products/camping-accessories/731077_81e5e594acef464f81511a5dab6d7b79~mv2.jpg", to: "/camping-accessories/quick-pitch-heavy-duty-ensuite-floor" },
  { name: "Shower Arm for K9 Roof Racks", price: 145, thumb: "/products/roofracks/731077_032aa2c72e7441acaf7f23d20cfa046e~mv2.jpg", to: "/roof-racks/shower-arm-k9" },
  { name: "Ensuite to Load Bar Brackets", price: 59, thumb: "/products/roofracks/731077_ed17e2bbe3de4844a0355f67d7fafb41~mv2.png", to: "/roof-racks/ensuite-to-load-bar-brackets" },
  { name: "Ensuite to Roof Tent Brackets", price: 45, thumb: "/products/roofracks/731077_f0536cd8b96c435f9f2a7bd9d672acff~mv2.webp", to: "/roof-racks/ensuite-to-roof-tent-brackets" },
];

const WHY = [
  { icon: Clock, t: "8-Second Setup", d: "Genuine Quick Pitch frame design — near-instant to pitch and pack away." },
  { icon: ShieldCheck, t: "Real South African Build", d: "Premium dual-layer canvas and aluminium frames — not the flimsy copies common in NZ." },
  { icon: Droplets, t: "Full Privacy System", d: "Ensuites, weather covers and universal mounts to fit any roof tent, canopy or load bar setup." },
];

const Card = ({ item, testid }) => (
  <Link
    to={item.to}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={item.thumb} secondary={item.gallery?.[1]?.src} alt={item.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {item.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {item.badge}
          </span>
        </div>
      )}      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{item.name.toUpperCase()}</h3>
        {item.tagline && <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{item.tagline}</p>}
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">
            {item.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
            ${fmt(item.price)}
          </p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function EnsuitesPage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/ensuite`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  const nativeItems = products.map((p) => ({ ...p, to: `/ensuites/${p.slug}` }));
  const allItems = [...nativeItems, ...ENSUITE_LINKED_PRODUCTS].sort((a, b) => b.price - a.price);

  return (
    <div data-testid="ensuites-page">
      <SEO
        path="/ensuites"
        title="Ensuites & Shower Tents NZ · Quick Pitch & EEZI-AWN"
        description="Genuine Quick Pitch Quick Ensuite shower/privacy tents, the EEZI-AWN Cube, and every bracket, floor and weather cover to mount them to your rig. Real South African build, not generic copies."
        image={ENSUITES_HERO}
      />

      {/* HERO */}
      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={ENSUITES_HERO} alt="Quick Pitch Quick Ensuite shower tent" className="w-full h-full object-cover" style={{ objectPosition: "50% 30%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-10% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Ensuites & Related Products</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            PRIVACY, ANYWHERE<br />
            <span className="text-[var(--brand-primary)]">YOU CAMP.</span>
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Genuine South African Quick Pitch and EEZI-AWN shower/privacy tents — plus every bracket, floor and weather cover to mount one to your rig.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#range" data-testid="ensuites-cta-range" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
              <a href="#enquire" className="btn-secondary">Enquire</a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`ensuites-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* GRID */}
      <section id="range" className="container-x pb-16 md:pb-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— The Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              ENSUITES & <span className="text-[var(--brand-primary)]">MOUNTING KIT.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">High to low · Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allItems.map((item, i) => <Card key={item.to} item={item} testid={`ensuite-item-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
